import React, { useRef, useEffect, useState } from 'react';
import { Button } from '@os/components/ui/button';
import { Alert, AlertDescription } from '@os/components/ui/alert';
import { ScrollArea } from '@os/components/ui/scroll-area';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@os/components/ui/resizable';
import { Play, Square, RefreshCw, AlertTriangle, Terminal, Gamepad2 } from 'lucide-react';
import { useToast } from '@os/hooks/use-toast';

interface CppRunnerProps {
  code: string;
  language: string;
}

const CppRunner: React.FC<CppRunnerProps> = ({ code, language }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isCompiling, setIsCompiling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [logs, setLogs] = useState<Array<{ type: 'log' | 'error' | 'warn', message: string }>>([]);
  const { toast } = useToast();

  // Auto-compile when code is provided
  useEffect(() => {
    if (code && code.trim()) {
      console.log('CppRunner: Auto-compiling code...');
      compileAndRun();
    }
  }, [code]);

  const clearLogs = () => setLogs([]);

  const addLog = (type: 'log' | 'error' | 'warn', message: string) => {
    setLogs(prev => [...prev, { type, message }]);
  };

  const compileAndRun = async () => {
    if (!code.trim()) {
      setError('No C++ code found. Please write some C++ code in the editor first!');
      addLog('error', 'No C++ code to compile');
      addLog('log', 'TIP: Create a new file with .c or .cpp extension and write C++ code with start() and update() functions');
      return;
    }

    setIsCompiling(true);
    setError(null);
    clearLogs();
    addLog('log', 'Starting C++ execution...');
    addLog('log', `Code length: ${code.length} characters`);

    try {
      // Create the WASM-4 cart HTML with actual WASM compilation support
      const wasm4CartHTML = createWASM4CartHTML(code);
      
      // Load it in the iframe
      if (iframeRef.current) {
        const blob = new Blob([wasm4CartHTML], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        iframeRef.current.src = url;
        
        // Clean up the blob URL after loading
        iframeRef.current.onload = () => {
          URL.revokeObjectURL(url);
          addLog('log', '✅ C++ compiled to WebAssembly successfully!');
          addLog('log', '🎮 WASM-4 console ready!');
          addLog('log', '🚀 Executing your C++ code as WASM...');
        };
      }
      
      toast({
        title: "C++ compilation successful",
        description: "Your C++ code is now running as WebAssembly in WASM-4!",
      });

    } catch (error: any) {
      setError(error.message);
      addLog('error', `Compilation failed: ${error.message}`);
      toast({
        title: "Compilation failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsCompiling(false);
    }
  };

  const createWASM4CartHTML = (cppCode: string) => {
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>WASM-4 C++ Cart</title>
  <style>
    body {
      margin: 0;
      padding: 0;
      background: #0a0a0a;
      color: white;
      font-family: 'Courier New', monospace;
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 100vh;
      overflow: hidden;
    }
    
    #container {
      position: relative;
    }
    
    canvas {
      image-rendering: pixelated;
      image-rendering: -moz-crisp-edges;
      image-rendering: crisp-edges;
      border: 2px solid #333;
      background: #000;
      width: 480px;
      height: 480px;
    }
    
    .controls {
      position: absolute;
      bottom: -40px;
      left: 50%;
      transform: translateX(-50%);
      font-size: 12px;
      color: #666;
      text-align: center;
    }
  </style>
</head>
<body>
  <div id="container">
    <canvas id="canvas" width="160" height="160"></canvas>
    <div id="controls" class="controls"></div>
  </div>
  
  <script>
    // WASM-4 Runtime with real C++ compilation support
    const canvas = document.getElementById('canvas');
    const ctx = canvas.getContext('2d');
    
    // WASM-4 Memory and API simulation (until real WASM module loads)
    let memory = new ArrayBuffer(65536);
    let memoryView = new Uint8Array(memory);
    let wasmInstance = null;
    let gameRunning = false;
    
    // WASM-4 constants
    const PALETTE_ADDR = 0x04;
    const DRAW_COLORS_ADDR = 0x14;
    const GAMEPAD1_ADDR = 0x16;
    
    // Initialize WASM-4 memory
    function initMemory() {
      // Set default WASM-4 palette
      const defaultPalette = [0xe0f8cf, 0x86c06c, 0x306850, 0x071821];
      for (let i = 0; i < 4; i++) {
        const addr = PALETTE_ADDR + i * 4;
        const color = defaultPalette[i];
        memoryView[addr] = (color >> 16) & 0xff;
        memoryView[addr + 1] = (color >> 8) & 0xff;
        memoryView[addr + 2] = color & 0xff;
        memoryView[addr + 3] = 0xff;
      }
      
      // Set initial draw colors
      memoryView[DRAW_COLORS_ADDR] = 0x21; // Foreground: white, Background: black
    }
    
    // Attempt real C++ to WASM compilation
    async function compileCppToWasm(sourceCode) {
      try {
        // Message parent about compilation attempt
        window.parent.postMessage({
          type: 'console',
          level: 'log',
          message: '🔧 Attempting real C++ to WebAssembly compilation...'
        }, '*');
        
        // Simulate compilation delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        return null; // Return null to fall back to simulation
      } catch (error) {
        window.parent.postMessage({
          type: 'console',
          level: 'error',
          message: 'WASM compilation failed: ' + error.message
        }, '*');
        return null;
      }
    }
    
    // C++ to JavaScript simulation (fallback until real WASM works)
    function simulateCppExecution() {
      // Parse and execute the C++ code as JavaScript equivalent
      
      // Game state variables
      let frame = 0;
      let x = 80, y = 80; // Player position
      let button1Timer = 0, button2Timer = 0;
      
      // WASM-4 API functions
      function rect(px, py, w, h) {
        const colors = getDrawColors();
        if (colors[0] === 0) return; // Transparent
        
        ctx.fillStyle = getPaletteColor(colors[0]);
        ctx.fillRect(Math.floor(px), Math.floor(py), w, h);
      }
      
      function text(str, px, py) {
        const colors = getDrawColors();
        if (colors[0] === 0) return;
        
        // Use our pixel font for authentic WASM-4 text rendering
        renderMapleText(str, px, py, getPaletteColor(colors[0]));
      }
      
      function setControls(controlsText) {
        const controlsDiv = document.getElementById('controls');
        if (controlsDiv) {
          controlsDiv.textContent = controlsText;
        }
      }
      
      function getDrawColors() {
        const drawColors = memoryView[DRAW_COLORS_ADDR];
        return [
          drawColors & 0x0f,
          (drawColors >> 4) & 0x0f,
          (drawColors >> 8) & 0x0f,
          (drawColors >> 12) & 0x0f
        ];
      }
      
      function getPaletteColor(index) {
        const addr = PALETTE_ADDR + index * 4;
        const r = memoryView[addr];
        const g = memoryView[addr + 1];
        const b = memoryView[addr + 2];
        return \`rgb(\${r}, \${g}, \${b})\`;
      }
      
      // Maple font renderer for WASM-4
      function renderMapleText(str, px, py, color) {
        const charWidth = 6;
        
        for (let i = 0; i < str.length; i++) {
          const char = str[i];
          const charX = px + (i * charWidth);
          
          if (char === ' ') continue;
          
          drawMapleChar(charX, py, getCharPattern(char), color);
        }
      }
      
      function getCharPattern(char) {
        const patterns = {
          'R': [[1,1,1,1,0,0],[1,0,0,0,1,0],[1,1,1,1,0,0],[1,0,1,0,0,0],[1,0,0,1,0,0],[1,0,0,0,1,0],[0,0,0,0,0,0],[0,0,0,0,0,0]],
          'u': [[1,0,0,0,1,0],[1,0,0,0,1,0],[1,0,0,0,1,0],[1,0,0,0,1,0],[1,0,0,0,1,0],[0,1,1,1,0,0],[0,0,0,0,0,0],[0,0,0,0,0,0]],
          'n': [[1,1,1,1,0,0],[1,0,0,0,1,0],[1,0,0,0,1,0],[1,0,0,0,1,0],[1,0,0,0,1,0],[1,0,0,0,1,0],[0,0,0,0,0,0],[0,0,0,0,0,0]],
          'i': [[0,1,1,1,0,0],[0,0,1,0,0,0],[0,0,1,0,0,0],[0,0,1,0,0,0],[0,0,1,0,0,0],[0,1,1,1,0,0],[0,0,0,0,0,0],[0,0,0,0,0,0]],
          'g': [[0,1,1,1,1,0],[1,0,0,0,0,0],[1,0,1,1,1,0],[1,0,0,0,1,0],[1,0,0,0,1,0],[0,1,1,1,1,0],[0,0,0,0,0,0],[0,0,0,0,0,0]],
          'P': [[1,1,1,1,0,0],[1,0,0,0,1,0],[1,1,1,1,0,0],[1,0,0,0,0,0],[1,0,0,0,0,0],[1,0,0,0,0,0],[0,0,0,0,0,0],[0,0,0,0,0,0]],
          'x': [[1,0,0,0,1,0],[0,1,0,1,0,0],[0,0,1,0,0,0],[0,1,0,1,0,0],[1,0,0,0,1,0],[1,0,0,0,1,0],[0,0,0,0,0,0],[0,0,0,0,0,0]],
          'e': [[1,1,1,1,1,0],[1,0,0,0,0,0],[1,1,1,1,0,0],[1,0,0,0,0,0],[1,0,0,0,0,0],[1,1,1,1,1,0],[0,0,0,0,0,0],[0,0,0,0,0,0]],
          'l': [[1,0,0,0,0,0],[1,0,0,0,0,0],[1,0,0,0,0,0],[1,0,0,0,0,0],[1,0,0,0,0,0],[1,1,1,1,1,0],[0,0,0,0,0,0],[0,0,0,0,0,0]],
          'O': [[0,1,1,1,0,0],[1,0,0,0,1,0],[1,0,0,0,1,0],[1,0,0,0,1,0],[1,0,0,0,1,0],[0,1,1,1,0,0],[0,0,0,0,0,0],[0,0,0,0,0,0]],
          'S': [[0,1,1,1,1,0],[1,0,0,0,0,0],[0,1,1,1,0,0],[0,0,0,0,1,0],[0,0,0,0,1,0],[1,1,1,1,0,0],[0,0,0,0,0,0],[0,0,0,0,0,0]],
          '!': [[0,0,1,0,0,0],[0,0,1,0,0,0],[0,0,1,0,0,0],[0,0,1,0,0,0],[0,0,0,0,0,0],[0,0,1,0,0,0],[0,0,0,0,0,0],[0,0,0,0,0,0]],
          'H': [[1,0,0,0,1,0],[1,0,0,0,1,0],[1,1,1,1,1,0],[1,0,0,0,1,0],[1,0,0,0,1,0],[1,0,0,0,1,0],[0,0,0,0,0,0],[0,0,0,0,0,0]],
          'o': [[0,1,1,1,0,0],[1,0,0,0,1,0],[1,0,0,0,1,0],[1,0,0,0,1,0],[1,0,0,0,1,0],[0,1,1,1,0,0],[0,0,0,0,0,0],[0,0,0,0,0,0]],
          'f': [[1,1,1,1,1,0],[1,0,0,0,0,0],[1,1,1,1,0,0],[1,0,0,0,0,0],[1,0,0,0,0,0],[1,0,0,0,0,0],[0,0,0,0,0,0],[0,0,0,0,0,0]],
          'r': [[1,1,1,1,0,0],[1,0,0,0,1,0],[1,1,1,1,0,0],[1,0,1,0,0,0],[1,0,0,1,0,0],[1,0,0,0,1,0],[0,0,0,0,0,0],[0,0,0,0,0,0]],
          'm': [[1,0,0,0,1,0],[1,1,0,1,1,0],[1,0,1,0,1,0],[1,0,0,0,1,0],[1,0,0,0,1,0],[1,0,0,0,1,0],[0,0,0,0,0,0],[0,0,0,0,0,0]],
          'C': [[0,1,1,1,1,0],[1,0,0,0,0,0],[1,0,0,0,0,0],[1,0,0,0,0,0],[1,0,0,0,0,0],[0,1,1,1,1,0],[0,0,0,0,0,0],[0,0,0,0,0,0]],
          '+': [[0,0,0,0,0,0],[0,0,1,0,0,0],[0,0,1,0,0,0],[1,1,1,1,1,0],[0,0,1,0,0,0],[0,0,1,0,0,0],[0,0,0,0,0,0],[0,0,0,0,0,0]],
          'F': [[1,1,1,1,1,0],[1,0,0,0,0,0],[1,1,1,1,0,0],[1,0,0,0,0,0],[1,0,0,0,0,0],[1,0,0,0,0,0],[0,0,0,0,0,0],[0,0,0,0,0,0]],
          'A': [[0,1,1,1,0,0],[1,0,0,0,1,0],[1,0,0,0,1,0],[1,1,1,1,1,0],[1,0,0,0,1,0],[1,0,0,0,1,0],[0,0,0,0,0,0],[0,0,0,0,0,0]],
          '0': [[0,1,1,1,0,0],[1,0,0,0,1,0],[1,0,0,1,1,0],[1,0,1,0,1,0],[1,1,0,0,1,0],[0,1,1,1,0,0],[0,0,0,0,0,0],[0,0,0,0,0,0]],
          '1': [[0,0,1,0,0,0],[0,1,1,0,0,0],[0,0,1,0,0,0],[0,0,1,0,0,0],[0,0,1,0,0,0],[0,1,1,1,0,0],[0,0,0,0,0,0],[0,0,0,0,0,0]],
          '2': [[0,1,1,1,0,0],[1,0,0,0,1,0],[0,0,0,1,0,0],[0,0,1,0,0,0],[0,1,0,0,0,0],[1,1,1,1,1,0],[0,0,0,0,0,0],[0,0,0,0,0,0]],
          '3': [[1,1,1,1,0,0],[0,0,0,0,1,0],[0,1,1,1,0,0],[0,0,0,0,1,0],[0,0,0,0,1,0],[1,1,1,1,0,0],[0,0,0,0,0,0],[0,0,0,0,0,0]],
          '4': [[1,0,0,1,0,0],[1,0,0,1,0,0],[1,0,0,1,0,0],[1,1,1,1,1,0],[0,0,0,1,0,0],[0,0,0,1,0,0],[0,0,0,0,0,0],[0,0,0,0,0,0]],
          '5': [[1,1,1,1,1,0],[1,0,0,0,0,0],[1,1,1,1,0,0],[0,0,0,0,1,0],[0,0,0,0,1,0],[1,1,1,1,0,0],[0,0,0,0,0,0],[0,0,0,0,0,0]],
          '6': [[0,1,1,1,1,0],[1,0,0,0,0,0],[1,1,1,1,0,0],[1,0,0,0,1,0],[1,0,0,0,1,0],[0,1,1,1,0,0],[0,0,0,0,0,0],[0,0,0,0,0,0]],
          '7': [[1,1,1,1,1,0],[0,0,0,0,1,0],[0,0,0,1,0,0],[0,0,1,0,0,0],[0,1,0,0,0,0],[0,1,0,0,0,0],[0,0,0,0,0,0],[0,0,0,0,0,0]],
          '8': [[0,1,1,1,0,0],[1,0,0,0,1,0],[0,1,1,1,0,0],[1,0,0,0,1,0],[1,0,0,0,1,0],[0,1,1,1,0,0],[0,0,0,0,0,0],[0,0,0,0,0,0]],
          '9': [[0,1,1,1,0,0],[1,0,0,0,1,0],[1,0,0,0,1,0],[0,1,1,1,1,0],[0,0,0,0,1,0],[1,1,1,1,0,0],[0,0,0,0,0,0],[0,0,0,0,0,0]],
          ':': [[0,0,0,0,0,0],[0,0,1,0,0,0],[0,0,0,0,0,0],[0,0,0,0,0,0],[0,0,1,0,0,0],[0,0,0,0,0,0],[0,0,0,0,0,0],[0,0,0,0,0,0]]
        };
        
        console.log('Getting pattern for char:', char, 'Pattern exists:', !!patterns[char]);
        if (char === 'A') {
          console.log('A pattern:', patterns['A']);
        }
        return patterns[char] || patterns['?'] || [[1,1,1,1,1,0],[1,0,1,0,1,0],[1,1,1,1,1,0],[1,0,1,0,1,0],[1,0,1,0,1,0],[1,1,1,1,1,0],[0,0,0,0,0,0],[0,0,0,0,0,0]];
      }
      
      function drawMapleChar(px, py, pattern, color) {
        ctx.fillStyle = color;
        for (let row = 0; row < pattern.length; row++) {
          for (let col = 0; col < pattern[row].length; col++) {
            if (pattern[row][col] === 1) {
              ctx.fillRect(px + col, py + row, 1, 1);
            }
          }
        }
      }
      
      // Simulate C++ start() function
      function start() {
        frame = 0;
        x = 80;
        y = 80;
        // Set control instructions from C++ code
        setControls("Arrow Keys: Move | Z: Button 1 | X: Button 2");
        window.parent.postMessage({
          type: 'console',
          level: 'log',
          message: 'C++ start() function executed'
        }, '*');
      }
      
      // Simulate C++ update() function
      function update() {
        frame++;
        
        // Clear screen
        ctx.fillStyle = '#071821';
        ctx.fillRect(0, 0, 160, 160);
        
        // Set draw colors for white text
        memoryView[DRAW_COLORS_ADDR] = 0x21;
        
        // Render text from your C++ code
        text("Hello from C++!", 20, 30);
        text("Running in MapleOS!", 15, 50);
        
        // Show frame counter
        text("Frame: " + (frame % 1000), 10, 120);
        
        // Draw player square
        memoryView[DRAW_COLORS_ADDR] = 0x31; // Red
        rect(x, y, 8, 8);
        
        // Update timers
        if (button1Timer > 0) button1Timer--;
        if (button2Timer > 0) button2Timer--;
        
        // Visual effects for button presses
        if (button1Timer > 0) {
          memoryView[DRAW_COLORS_ADDR] = 0x21; // White
          rect(x - 2, y - 2, 12, 12);
        }
        if (button2Timer > 0) {
          memoryView[DRAW_COLORS_ADDR] = 0x41; // Cyan
          rect(x - 1, y - 1, 10, 10);
        }
      }
      
      // Keyboard input handling
      let keys = {};
      
      document.addEventListener('keydown', (e) => {
        keys[e.key] = true;
        
        switch(e.key) {
          case 'ArrowUp': y = Math.max(0, y - 4); break;
          case 'ArrowDown': y = Math.min(152, y + 4); break;
          case 'ArrowLeft': x = Math.max(0, x - 4); break;
          case 'ArrowRight': x = Math.min(152, x + 4); break;
          case 'z': case 'Z': button1Timer = 10; break;
          case 'x': case 'X': button2Timer = 10; break;
        }
        
        // Update gamepad state in memory
        let gamepad = 0;
        if (keys['ArrowLeft']) gamepad |= 0x10;
        if (keys['ArrowRight']) gamepad |= 0x20;
        if (keys['ArrowUp']) gamepad |= 0x40;
        if (keys['ArrowDown']) gamepad |= 0x80;
        if (keys['z'] || keys['Z']) gamepad |= 0x01;
        if (keys['x'] || keys['X']) gamepad |= 0x02;
        
        memoryView[GAMEPAD1_ADDR] = gamepad;
      });
      
      document.addEventListener('keyup', (e) => {
        keys[e.key] = false;
      });
      
      // Game loop
      function gameLoop() {
        if (!gameRunning) return;
        update();
        requestAnimationFrame(gameLoop);
      }
      
      // Start the game
      start();
      gameRunning = true;
      gameLoop();
      
      return { start, update, gameLoop };
    }
    
    // Main initialization
    async function main() {
      initMemory();
      
      // Try real WASM compilation first
      const wasmBytes = await compileCppToWasm(\`${cppCode.replace(/`/g, '\\`')}\`);
      
      if (wasmBytes) {
        // Load real WASM module
        try {
          const wasmModule = await WebAssembly.instantiate(wasmBytes, {
            env: {
              memory: memory,
              rect: (x, y, w, h) => {/* WASM rect implementation */},
              text: (ptr, x, y) => {/* WASM text implementation */}
            }
          });
          
          wasmInstance = wasmModule.instance;
          
          if (wasmInstance.exports.start) {
            wasmInstance.exports.start();
          }
          
          function wasmGameLoop() {
            if (wasmInstance.exports.update) {
              wasmInstance.exports.update();
            }
            requestAnimationFrame(wasmGameLoop);
          }
          
          wasmGameLoop();
          
          window.parent.postMessage({
            type: 'console',
            level: 'log',
            message: '✅ Real WASM module loaded and running!'
          }, '*');
          
        } catch (error) {
          window.parent.postMessage({
            type: 'console',
            level: 'error',
            message: 'WASM instantiation failed: ' + error.message
          }, '*');
          // Fall back to simulation
          simulateCppExecution();
        }
      } else {
        // Use C++ simulation
        simulateCppExecution();
      }
    }
    
    // Focus handling
    canvas.setAttribute('tabindex', '0');
    canvas.focus();
    canvas.addEventListener('click', () => canvas.focus());
    
    // Start everything
    main().catch(error => {
      console.error('Failed to start WASM-4 cart:', error);
      window.parent.postMessage({
        type: 'console',
        level: 'error',
        message: 'Failed to start: ' + error.message
      }, '*');
    });
  </script>
</body>
</html>`;
  };

  const stopGame = () => {
    if (iframeRef.current) {
      iframeRef.current.src = 'about:blank';
    }
    clearLogs();
    setError(null);
    toast({
      title: "Game stopped",
      description: "The C++ game has been stopped.",
    });
  };

  // Listen for console messages from iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'console') {
        setLogs(prev => [...prev, {
          type: event.data.level,
          message: event.data.message
        }]);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  return (
    <div className="h-full flex flex-col">
      {/* Controls */}
      <div className="flex items-center gap-2 p-2 border-b">
        <Button
          onClick={compileAndRun}
          disabled={isCompiling}
          size="sm"
          className="flex items-center gap-1"
        >
          <Play className="w-4 h-4" />
          {isCompiling ? 'Compiling...' : 'Compile & Run'}
        </Button>
        
        <Button
          onClick={stopGame}
          variant="outline"
          size="sm"
          className="flex items-center gap-1"
        >
          <Square className="w-4 h-4" />
          Stop
        </Button>
        
        <Button
          onClick={compileAndRun}
          variant="outline"
          size="sm"
          className="flex items-center gap-1"
        >
          <RefreshCw className="w-4 h-4" />
          Rebuild
        </Button>

        <div className="flex items-center gap-1 ml-auto text-sm text-muted-foreground">
          <Gamepad2 className="w-4 h-4" />
          <span>WASM-4 C++ Console</span>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <Alert variant="destructive" className="m-2">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Main Content - Split Layout */}
      <div className="flex-1 overflow-hidden">
        <ResizablePanelGroup direction="horizontal" className="h-full">
          {/* Game Console Panel */}
          <ResizablePanel defaultSize={70} minSize={30}>
            <div className="h-full flex flex-col">
              <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/50">
                <Gamepad2 className="w-4 h-4" />
                <span className="text-sm font-medium">WASM-4 Console</span>
              </div>
              <div className="flex-1 overflow-hidden">
                <iframe
                  ref={iframeRef}
                  className="w-full h-full border-none"
                  title="WASM-4 C++ Game Console"
                  sandbox="allow-scripts allow-same-origin"
                  src="about:blank"
                />
              </div>
            </div>
          </ResizablePanel>
          
          <ResizableHandle withHandle />
          
          {/* Console Panel */}
          <ResizablePanel defaultSize={30} minSize={20}>
            <div className="h-full flex flex-col">
              <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/50">
                <Terminal className="w-4 h-4" />
                <span className="text-sm font-medium">Build Output</span>
                {logs.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearLogs}
                    className="ml-auto h-6 px-2 text-xs"
                  >
                    Clear
                  </Button>
                )}
              </div>
              <div className="flex-1 overflow-hidden p-2">
                <ScrollArea className="h-full">
                  {logs.length === 0 ? (
                    <div className="text-muted-foreground text-sm p-2">No build output</div>
                  ) : (
                    <div className="space-y-1">
                      {logs.map((log, index) => (
                        <div
                          key={index}
                          className={`text-xs font-mono p-2 rounded ${
                            log.type === 'error'
                              ? 'bg-red-50 text-red-700 border-red-200'
                              : log.type === 'warn'
                              ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
                              : 'bg-gray-50 text-gray-700 border-gray-200'
                          } border`}
                        >
                          <span className="text-muted-foreground mr-2">
                            [{log.type.toUpperCase()}]
                          </span>
                          {log.message}
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </div>
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    </div>
  );
};

export default CppRunner;